import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, FileText, TriangleAlert } from "lucide-react";
import { vault } from "@/lib/mockData";

function ReadinessRing({ value }) {
  const r = 56;
  const circ = 2 * Math.PI * r;
  const offset = circ - (value / 100) * circ;
  return (
    <div className="relative flex h-40 w-40 items-center justify-center">
      <svg viewBox="0 0 140 140" className="h-full w-full -rotate-90">
        <defs>
          <linearGradient id="ring-grad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#0a0a0a" />
            <stop offset="55%" stopColor="#6D28D9" />
            <stop offset="100%" stopColor="#22D3EE" />
          </linearGradient>
        </defs>
        <circle
          cx="70"
          cy="70"
          r={r}
          stroke="#E5E7EB"
          strokeWidth="10"
          fill="none"
        />
        <motion.circle
          cx="70"
          cy="70"
          r={r}
          stroke="url(#ring-grad)"
          strokeWidth="10"
          fill="none"
          strokeLinecap="round"
          strokeDasharray={circ}
          initial={{ strokeDashoffset: circ }}
          whileInView={{ strokeDashoffset: offset }}
          viewport={{ once: true, amount: 0.5 }}
          transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
        />
      </svg>
      <div className="absolute flex flex-col items-center">
        <div className="text-[36px] font-extrabold tracking-tight text-zinc-900">
          {value}%
        </div>
        <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-500">
          Readiness
        </div>
      </div>
    </div>
  );
}

export default function VaultTeaser() {
  const missing = [];
  vault.sections.forEach((s) =>
    s.docs.forEach((d) => {
      if (d.status === "missing")
        missing.push({ ...d, section: s.name });
    }),
  );

  return (
    <section
      data-testid="vault-teaser"
      id="vault"
      className="px-5 py-24 md:px-8 md:py-32"
    >
      <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-12 lg:grid-cols-2">
        {/* Visual */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.25 }}
          transition={{ duration: 0.5 }}
          className="card-soft order-2 rounded-[28px] p-7 lg:order-1"
        >
          <div className="flex flex-col items-center gap-4 md:flex-row md:items-start md:gap-6">
            <ReadinessRing value={vault.readinessPercent} />
            <div className="flex-1">
              <div className="eyebrow text-orange-700">
                {missing.length} missing documents
              </div>
              <ul className="mt-3 space-y-2">
                {missing.slice(0, 4).map((d) => (
                  <li
                    key={d.id}
                    data-testid={`vault-teaser-missing-${d.id}`}
                    className="flex items-center gap-2.5 rounded-xl bg-[#FFF7EE] px-3 py-2"
                  >
                    <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg bg-orange-100 text-orange-700">
                      <FileText className="h-3.5 w-3.5" strokeWidth={2.4} />
                    </span>
                    <span className="flex-1 truncate text-[12px] font-semibold text-zinc-800">
                      {d.name}
                    </span>
                    <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-orange-700">
                      {d.section}
                    </span>
                  </li>
                ))}
              </ul>
              <div className="mt-4 flex items-center gap-1.5 text-[11px] text-zinc-500">
                <TriangleAlert className="h-3 w-3 text-orange-500" />
                Vault flags missing files before your next investor call.
              </div>
            </div>
          </div>
        </motion.div>

        {/* Copy */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.5 }}
          className="order-1 lg:order-2"
        >
          <div className="eyebrow">File</div>
          <h2 className="mt-3 text-[40px] font-black leading-[1.02] tracking-tight text-zinc-900 md:text-[60px]">
            Your investor-ready<br />file. Always.
          </h2>
          <p className="mt-5 max-w-lg text-[15px] leading-relaxed text-zinc-600 md:text-[16px]">
            Vault keeps your pitch deck, financials, legal, and product assets in
            one place — and tells you exactly what's missing before your next
            investor call.
          </p>
          <Link
            to="/vault"
            data-testid="vault-teaser-cta"
            className="group mt-7 inline-flex items-center gap-1.5 text-[14px] font-semibold text-zinc-900 hover:text-black"
          >
            Open Venture Vault
            <ArrowRight
              className="h-4 w-4 transition-transform group-hover:translate-x-0.5"
              strokeWidth={2.4}
            />
          </Link>
        </motion.div>
      </div>
    </section>
  );
}
